'use client';

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  BUSINESS_UNIT_OVERFLOW_CHIP_WIDTH_PX,
  calculateVisibleTagCount,
} from './business-unit-selector.model';

const readOffsetWidth = (element: Element | null): number | null =>
  element instanceof HTMLElement ? element.offsetWidth : null;

export const useBusinessUnitTagOverflow = ({
  selectedCount,
  valueFingerprint,
  inputReservedWidthPx,
  remeasureKey,
}: {
  selectedCount: number;
  valueFingerprint: string;
  inputReservedWidthPx: number;
  remeasureKey: string;
}) => {
  const controlRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleTagCount, setVisibleTagCount] = useState(selectedCount);

  const updateVisibleTagCount = useCallback(() => {
    const control = controlRef.current;
    const measure = measureRef.current;

    if (!control || !measure) {
      return;
    }

    const tagWidths = Array.from(
      measure.querySelectorAll('[data-measure-tag]'),
    ).map((element) => readOffsetWidth(element) ?? 0);
    const fallbackOverflowWidth =
      readOffsetWidth(measure.querySelector('[data-measure-overflow]')) ??
      BUSINESS_UNIT_OVERFLOW_CHIP_WIDTH_PX;
    const overflowChipWidth =
      readOffsetWidth(
        measure.querySelector(
          `[data-measure-overflow="${Math.max(0, selectedCount - 1)}"]`,
        ),
      ) ?? fallbackOverflowWidth;

    setVisibleTagCount(
      calculateVisibleTagCount({
        controlWidth: control.clientWidth,
        tagWidths,
        overflowChipWidth,
        inputReservedWidthPx,
      }),
    );
  }, [selectedCount, inputReservedWidthPx]);

  useLayoutEffect(() => {
    updateVisibleTagCount();
  }, [updateVisibleTagCount, valueFingerprint, remeasureKey]);

  useEffect(() => {
    const control = controlRef.current;

    if (!control) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => updateVisibleTagCount());

    resizeObserver.observe(control);

    return () => resizeObserver.disconnect();
  }, [updateVisibleTagCount, valueFingerprint]);

  return { controlRef, measureRef, visibleTagCount };
};
